/* ==========================================================================
   Yummy Gummy — Clinical Study strip + drawer
   --------------------------------------------------------------------------
   Two independent behaviours, both guarded so a missing piece never throws:

   1. Strip line rotation — cross-fades the [data-pdp-clinical-line]s inside
      each [data-pdp-clinical-lines] on a timer. No rotation when there is a
      single line or under prefers-reduced-motion. Pauses on hover/focus.

   2. Drawer open/close — mirrors assets/pdp-facts-drawer.js exactly (class
      toggle + a yielded frame so the transition triggers, trapFocus/
      removeTrapFocus from assets/global.js which is loaded sitewide as a
      classic script, the .overflow-hidden body-lock utility, Esc to close,
      overlay/close-button click). The trigger names its target drawer by id
      (data-pdp-clinical-target) so multiple section instances never cross-
      wire.

   Per-element guards (__csLinesBound / __csDrawerBound) + init on
   DOMContentLoaded and shopify:section:load. No globals, no frameworks.
   ========================================================================== */
(function () {
  'use strict';

  var ROTATE_MS = 2200;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* --- 1. Strip line rotation ------------------------------------------- */
  function initLines(scope) {
    var groups = Array.prototype.slice.call(scope.querySelectorAll('[data-pdp-clinical-lines]'));
    groups.forEach(function (group) {
      if (group.__csLinesBound) return;
      group.__csLinesBound = true;

      var lines = Array.prototype.slice.call(group.querySelectorAll('[data-pdp-clinical-line]'));
      if (lines.length < 2 || prefersReducedMotion()) return;

      var index = 0;
      var timer = null;

      function show(next) {
        lines[index].classList.remove('is-active');
        index = (next + lines.length) % lines.length;
        lines[index].classList.add('is-active');
      }

      function start() {
        if (timer) return;
        timer = window.setInterval(function () { show(index + 1); }, ROTATE_MS);
      }
      function stop() {
        if (!timer) return;
        window.clearInterval(timer);
        timer = null;
      }

      // Pause while the user is reading (hover/focus on the strip button).
      var strip = group.closest('.pdp-clin__strip') || group;
      strip.addEventListener('mouseenter', stop);
      strip.addEventListener('mouseleave', start);
      strip.addEventListener('focusin', stop);
      strip.addEventListener('focusout', start);

      start();
    });
  }

  /* --- 2. Drawer open/close --------------------------------------------- */
  function initDrawers(scope) {
    var triggers = Array.prototype.slice.call(scope.querySelectorAll('[data-pdp-clinical-trigger]'));
    triggers.forEach(function (trigger) {
      if (trigger.__csDrawerBound) return;
      trigger.__csDrawerBound = true;

      var targetId = trigger.getAttribute('data-pdp-clinical-target');
      var drawer = targetId ? document.getElementById(targetId) : null;
      if (!drawer) return;

      var panel = drawer.querySelector('.pdp-clin__panel');
      var openedBy = null;
      var scrollY = 0;

      function open() {
        openedBy = trigger;
        drawer.hidden = false;
        // Yield a frame so the CSS transition reliably fires (same note as
        // cart-drawer.js / pdp-facts-drawer.js).
        window.setTimeout(function () { drawer.classList.add('is-active'); });
        // .overflow-hidden puts overflow:hidden on <body>, which makes the
        // browser forget the scroll position — closing the drawer then dropped
        // the reader back at the top of the page. Held here, restored on close.
        scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
        document.body.classList.add('overflow-hidden');

        drawer.addEventListener('transitionend', function onEnd() {
          if (typeof trapFocus === 'function' && panel) trapFocus(panel, panel);
        }, { once: true });
      }

      function close() {
        drawer.classList.remove('is-active');
        document.body.classList.remove('overflow-hidden');

        // Focus before restoring, not after: removeTrapFocus calls .focus()
        // without preventScroll, so it scrolls the trigger into view and would
        // undo a restore that ran first. The rAF lands after that.
        if (typeof removeTrapFocus === 'function') removeTrapFocus(openedBy);
        window.scrollTo(0, scrollY);
        window.requestAnimationFrame(function () { window.scrollTo(0, scrollY); });

        drawer.addEventListener('transitionend', function onEnd() {
          if (!drawer.classList.contains('is-active')) drawer.hidden = true;
        }, { once: true });
      }

      trigger.addEventListener('click', open);
      drawer.querySelectorAll('[data-pdp-clinical-close]').forEach(function (el) {
        el.addEventListener('click', close);
      });
      drawer.addEventListener('keyup', function (e) {
        if (e.code === 'Escape') close();
      });
    });
  }

  function initAll(scope) {
    var root = scope || document;
    try { initLines(root); } catch (e) {}
    try { initDrawers(root); } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); }, { once: true });
  } else {
    initAll(document);
  }
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
